import {
    AddressFragment,
    CreateAddressInput,
    OrderAddress,
    GetEligiblePaymentMethodsQuery,
} from "../../../common/generated-types";
import {
    ActiveOrderSnapshot,
    BillingCountryAddressLike,
    CheckoutPaymentOrderLine,
    CheckoutPaymentSupplierGroup,
    BillingAddressView,
} from "../../checkout.types";
import { toCreateAddressInput } from "../../../shared/utils/address.utils";
import { PAYMENT_METHODS } from "../../../common/constants";
import {
    CHECKOUT_PAYMENT_FACET,
    CHECKOUT_PAYMENT_GROUPING,
    CHECKOUT_PAYMENT_SUPPLY,
} from "./checkout-payment.constants";

export function billingCountryLabel(addr: BillingCountryAddressLike): string {
    if (typeof addr?.country === "string") {
        return addr.country;
    }
    return addr?.country?.name ?? "";
}

export function formatShippingAddressOption(address: AddressFragment): string {
    const parts = [
        address.streetLine1,
        address.city,
        address.province,
        address.postalCode,
        address.country?.name,
    ].filter((p) => p && String(p).trim());
    return parts.join(", ");
}

export function orderAddressMatchesSaved(
    orderAddr: Pick<OrderAddress, "streetLine1" | "postalCode" | "city">,
    saved: AddressFragment
): boolean {
    const norm = (v: string | undefined) =>
        String(v ?? "").trim().toLowerCase();
    return (
        norm(orderAddr.streetLine1) === norm(saved.streetLine1) &&
        norm(orderAddr.postalCode) === norm(saved.postalCode) &&
        norm(orderAddr.city) === norm(saved.city)
    );
}

export function getMonths(): number[] {
    return Array.from({ length: 12 }, (_, i) => i + 1);
}

export function getYears(): number[] {
    const year = new Date().getFullYear();
    return Array.from({ length: 10 }, (_, i) => year + i);
}

export function getEnabledPaymentMethods(
    paymentMethods: GetEligiblePaymentMethodsQuery["eligiblePaymentMethods"] | null | undefined
) {
    if (!paymentMethods) return [];
    return paymentMethods.filter((method) => method.isEligible);
}

export function getFilteredPaymentMethods(
    paymentMethods: GetEligiblePaymentMethodsQuery["eligiblePaymentMethods"] | null | undefined,
    isStripePaymentMethod: (code: string) => boolean
) {
    if (!paymentMethods) return [];
    return [...paymentMethods]
        .filter(
            (method) =>
                method.isEligible &&
                method.code.toLowerCase() !== PAYMENT_METHODS.CONNECTED_PAYMENT
        )
        .sort((a, b) => {
            if (isStripePaymentMethod(a.code)) return -1;
            if (isStripePaymentMethod(b.code)) return 1;
            return 0;
        });
}

export function getAvailableStock(line: CheckoutPaymentOrderLine): number {
    const stockLevel = line.productVariant?.stockLevels?.[0];
    if (stockLevel) {
        return (stockLevel.stockOnHand || 0) - (stockLevel.stockAllocated || 0);
    }
    return 0;
}

export function getBrand(line: CheckoutPaymentOrderLine): string {
    const brandFacetValue = line.productVariant.facetValues?.find(
        (fv) => fv.facet.code === CHECKOUT_PAYMENT_FACET.BRAND
    );
    return brandFacetValue ? brandFacetValue.name : "";
}

export function isPreOrder(group: CheckoutPaymentSupplierGroup): boolean {
    return (
        group.supplyType?.toLowerCase() ===
        CHECKOUT_PAYMENT_SUPPLY.PRE_ORDER_NORMALIZED
    );
}

export function getSupplierTitle(group: CheckoutPaymentSupplierGroup): string {
    return `Supplier #${group.supplierId}`;
}

export function valueToAddressInput(value: Partial<CreateAddressInput> | null | undefined): CreateAddressInput {
    return toCreateAddressInput(value);
}

export function orderAddressesMatch(
    a?: Pick<OrderAddress, "streetLine1" | "postalCode" | "city" | "fullName"> | null,
    b?: Pick<OrderAddress, "streetLine1" | "postalCode" | "city" | "fullName"> | null
): boolean {
    if (!a?.streetLine1 || !b?.streetLine1) {
        return false;
    }
    const norm = (v: string | undefined) =>
        String(v ?? "").trim().toLowerCase();
    return (
        norm(a.streetLine1) === norm(b.streetLine1) &&
        norm(a.postalCode) === norm(b.postalCode) &&
        norm(a.city) === norm(b.city) &&
        norm(a.fullName) === norm(b.fullName)
    );
}

export function isBillingSameAsShipping(
    activeOrder: ActiveOrderSnapshot | null | undefined
): boolean {
    const bill = activeOrder?.billingAddress;
    const ship = activeOrder?.shippingAddress;
    if (!bill?.streetLine1 && !bill?.city) {
        return true;
    }
    return orderAddressesMatch(bill, ship);
}

export function orderAddressToCreateInput(
    addr: OrderAddress | null | undefined
): CreateAddressInput {
    return valueToAddressInput({
        fullName: addr?.fullName ?? "",
        company: addr?.company ?? "",
        streetLine1: addr?.streetLine1 ?? "",
        streetLine2: addr?.streetLine2 ?? "",
        city: addr?.city ?? "",
        province: addr?.province ?? "",
        postalCode: addr?.postalCode ?? "",
        phoneNumber: addr?.phoneNumber ?? "",
        countryCode: addr?.countryCode ?? "",
    });
}

export function getBillingView(
    activeOrder: ActiveOrderSnapshot,
    addresses: AddressFragment[]
): BillingAddressView | null {
    const orderBill = activeOrder?.billingAddress;
    if (orderBill && (orderBill.streetLine1 || orderBill.city)) {
        return {
            fullName: orderBill.fullName,
            company: orderBill.company,
            streetLine1: orderBill.streetLine1,
            streetLine2: orderBill.streetLine2,
            city: orderBill.city,
            province: orderBill.province,
            postalCode: orderBill.postalCode,
            countryLabel: billingCountryLabel(orderBill),
        };
    }
    if (addresses?.length) {
        const p = addresses.find((a) => a.defaultBillingAddress);
        if (p && (p.streetLine1 || p.city)) {
            return {
                fullName: p.fullName,
                company: p.company,
                streetLine1: p.streetLine1,
                streetLine2: p.streetLine2,
                city: p.city,
                province: p.province,
                postalCode: p.postalCode,
                countryLabel: billingCountryLabel(p),
            };
        }
    }
    const ship = activeOrder?.shippingAddress;
    if (ship && (ship.streetLine1 || ship.city)) {
        return {
            fullName: ship.fullName,
            company: ship.company,
            streetLine1: ship.streetLine1,
            streetLine2: ship.streetLine2,
            city: ship.city,
            province: ship.province,
            postalCode: ship.postalCode,
            countryLabel: billingCountryLabel(ship),
        };
    }
    return null;
}

export function groupLinesBySupplier(lines: CheckoutPaymentOrderLine[] | undefined | null): CheckoutPaymentSupplierGroup[] {
    if (!lines) return [];

    const groupedLines = lines.reduce(
        (groups: Record<string, CheckoutPaymentSupplierGroup>, line) => {
            const typedLine = line as CheckoutPaymentOrderLine;
            const supplierId =
                typedLine.productVariant.pricing?.id ??
                CHECKOUT_PAYMENT_GROUPING.UNKNOWN_SUPPLIER_ID;
            const supplierName =
                typedLine.productVariant.sku ??
                CHECKOUT_PAYMENT_GROUPING.UNKNOWN_SUPPLIER_LABEL;

            if (!groups[supplierId]) {
                groups[supplierId] = {
                    supplierId,
                    supplierName,
                    lines: [],
                    totalValue: 0,
                    shippingDate: typedLine.productVariant.pricing?.shippingDate ?? "",
                    preOrderShippingDate:
                        typedLine.productVariant.preOrderShippingDate ?? "",
                    supplyType: typedLine.productVariant.pricing?.supplyType ?? "",
                };
            }
            groups[supplierId].lines.push(typedLine);
            groups[supplierId].totalValue +=
                typedLine.discountedLinePriceWithTax ?? typedLine.linePriceWithTax;

            return groups;
        },
        {} as Record<string, CheckoutPaymentSupplierGroup>
    );

    return Object.values(groupedLines);
}
